/**
 * S9b — shared MLEADER leader-line break-gap splitter.
 *
 * A MULTILEADER `LEADER_LINE` may carry break ranges (ezdxf `LeaderLine.breaks`
 * — DXF CONTEXT_DATA group 90 break_index + 11/21/31 start + 12/22/32 end).
 * AutoCAD draws the leader stroke up to each break's START point, leaves a gap,
 * and resumes at its END point (so an underlying object / dimension shows
 * through the leader).
 *
 * # One truth for two render seams
 *
 * Both the SVG renderer (`scene/renderMultileader.tsx`) and the WebGL geometry
 * builder (`multileaderGeometry.ts` → `batchedLayer.ts` `"multileader"` case)
 * MUST split identically, else the two backends disagree on where the stroke
 * breaks. This module is the single split truth both call — mirroring the
 * shared `catmullRom.ts` spline sampler both already share. Codex S9b P2
 * ("Preserve MLEADER break gaps in WebGL"): before this, the shared builder was
 * break-agnostic and the WebGL leader ran straight through the intended gap.
 *
 * # Approximation (documented, shared)
 *
 * Each break is placed on the polyline segment whose MIDPOINT is nearest the
 * break START point. AutoCAD computes exact arc-length offsets along the
 * (possibly splined) leader; the midpoint-nearest rule is a faithful visual
 * approximation for the straight/spline control polyline and — crucially —
 * keeps SVG and WebGL byte-consistent because they share THIS function.
 */

/** One break range along a leader line (start → end gap). */
export interface LeaderBreak {
  start: { x: number; y: number };
  end: { x: number; y: number };
}

/**
 * Split `vertices` into the sub-polylines that survive the break gaps, in
 * draw order. Generic over the point type `P` so the SVG renderer (2-D
 * `StagePoint`) and the WebGL builder (`Vec3`, z carried) share it — the
 * inserted break endpoints are constructed via `makePoint` so each backend
 * keeps its own point shape.
 *
 * Contract:
 *   - `breaks` empty  → `[[...vertices]]` when `vertices.length >= 2`, else `[]`.
 *   - each break splits the CURRENT working polyline at the nearest segment:
 *     the run up to and including the break START is emitted, the remainder
 *     restarts at the break END.
 *   - a break that matches no segment (`bestIdx === -1`, e.g. a 1-vertex
 *     working run) is skipped WITHOUT dropping the stroke.
 *
 * Returned sub-polylines may contain a 1-vertex tail; callers draw only those
 * with `>= 2` points (a single point is not a stroke).
 */
export function splitLeaderLineAtBreaks<P extends { x: number; y: number }>(
  vertices: readonly P[],
  breaks: readonly LeaderBreak[],
  makePoint: (x: number, y: number) => P,
): P[][] {
  if (breaks.length === 0) {
    return vertices.length >= 2 ? [[...vertices]] : [];
  }
  const out: P[][] = [];
  let working: P[] = [...vertices];
  for (const bp of breaks) {
    // Find the working segment whose midpoint is nearest the break start.
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < working.length - 1; i++) {
      const a = working[i]!;
      const b = working[i + 1]!;
      const segMidX = (a.x + b.x) / 2;
      const segMidY = (a.y + b.y) / 2;
      const d = Math.hypot(segMidX - bp.start.x, segMidY - bp.start.y);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx === -1) continue;
    const breakStart = makePoint(bp.start.x, bp.start.y);
    const breakEnd = makePoint(bp.end.x, bp.end.y);
    out.push(working.slice(0, bestIdx + 1).concat([breakStart]));
    working = [breakEnd].concat(working.slice(bestIdx + 1));
  }
  if (working.length >= 2) out.push(working);
  return out;
}
